// CheckinFlow's copy, in one place so the component and its suite reference
// the same string instead of each holding a copy-pasted copy of it.
//
// The therapist name is a runtime slot, so lines carrying it are FUNCTIONS. No
// line OPENS with the slot: the "your therapist" fallback is lowercase and
// cannot start a sentence (G8).
//
// No line promises the therapist reads these answers: no therapist surface does
// yet. The privacy line says so plainly, and changes when that view ships.

import type { ExpectedOutcome } from "../../expectedOutcome";

/** Used when the couple has no therapist name loaded. Lowercase by design (G8). */
export const FALLBACK_THERAPIST_NAME = "your therapist";

/** Who sees a partner's answers today. Shown on the first step, before anything is written. */
export function privacyLine(therapistName: string): string {
  return `Only you and Dova see these answers. Your partner doesn't, and neither does ${therapistName}.`;
}

/**
 * The EXPECTED outcome behind a CLOSED check-in window: a business answer, not a failure.
 *
 * Both routes into it share this copy: the `window_open: false` the GET reports, and the 409 the
 * PUT raises. `checkin_window_open` is false once the session has started, and for a CANCELED or
 * COMPLETED session, and the wire does not say which. So the lines state the RULE and name no
 * cause, the same constraint `FEEDBACK_CLOSED` documents.
 */
export const CHECKIN_CLOSED: ExpectedOutcome = {
  title: "Check-in is closed for this session",
  body: "Check-ins close once a session starts, and stay closed for a session that was canceled.",
  nextStep: (therapistName: string) =>
    `Nothing has been saved. If something can’t wait, reach out to ${therapistName}.`,
};

/** One written question: its own screen, optional, saved as `field`. */
export interface CheckinQuestion {
  field: "onYourMind" | "showedPartnerMatters" | "partnerShowedIMatter";
  title: string;
  placeholder: string;
}

export const CHECKIN_COPY = {
  continueLabel: "Continue",
  saveLabel: "Save Answers",
  backLabel: "Back",
  optionalHint: "Optional",

  mood: {
    title: "How are you feeling?",
    // Shape-neutral on purpose: this said "square" while the grid rendered
    // rounded cells, and it kept saying it after they became circles. The grid
    // is the only thing on the screen to tap, so the sentence loses nothing by
    // not naming a shape — and it stops going stale when the design moves.
    sub: "Tap the one that fits, then the word that's closest.",
    wordPrompt: "And the word that fits best",
  },

  /** Screens 2–4, in order. Every one is optional. */
  questions: [
    {
      field: "onYourMind",
      title: "What's on your mind going into this session?",
      placeholder: "A worry, a hope, something from this week…",
    },
    {
      field: "showedPartnerMatters",
      title: "What's one thing you did this week to show your partner they matter?",
      placeholder: "Small things count: a text, a hug, really listening…",
    },
    {
      field: "partnerShowedIMatter",
      title: "What's one thing your partner did this week that made you feel like you matter?",
      placeholder: "Something they said or did, big or small…",
    },
  ] satisfies readonly CheckinQuestion[],

  saved: {
    /** Names the session when its day is known ("Friday"), so the screen confirms WHICH
     *  check-in went in; the plain result when the session read hasn't landed. */
    title: (weekday: string | null) =>
      weekday ? `You've checked in for ${weekday}'s session.` : "Your check-in is saved.",
    // The privacy line lives on the first step, where someone decides what to share; here it
    // would only repeat itself. The answers feed the between-session coaching prompts.
    body: "Your answers will help shape your coaching between sessions.",
    homeLabel: "Back to home",
  },
} as const;
